import React, { useState } from 'react';
import {
  Modal, Form, Input, Button, Space, Typography, Alert,
  Card, message, Checkbox, Divider, Descriptions
} from 'antd';
import {
  StopOutlined, WarningOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import { appointmentApi, Appointment } from '../../services/appointmentApi';
import { getTokens } from '../../services/authApi';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface CancelAppointmentModalProps {
  visible: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CancelAppointmentModal: React.FC<CancelAppointmentModalProps> = ({
  visible,
  appointment,
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values: any) => {
    if (!appointment?.id) return;

    if (!user?.id) {
      message.error('Unable to determine current user — please re-login.');
      return;
    }

    setSubmitting(true);
    try {
      await appointmentApi.cancel(
        appointment.id,
        user.id,
        values.cancellation_reason,
        values.notify_customer !== false
      );

      message.success('Appointment cancelled successfully');
      form.resetFields();
      if (onSuccess) onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error cancelling appointment:', error);
      message.error('Failed to cancel appointment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  if (!appointment) return null;

  return (
    <Modal
      title={
        <Space>
          <StopOutlined />
          <span>Cancel Appointment</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={600}
      footer={null}
    >
      {/* Warning Alert */}
      <Alert
        message="Warning: This action cannot be undone"
        description="Cancelling this appointment will notify the customer and update the schedule. Consider rescheduling instead if the appointment can be moved to another time."
        type="warning"
        showIcon
        icon={<ExclamationCircleOutlined />}
        style={{ marginBottom: 16 }}
      />

      {/* Appointment Info */}
      <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Appointment">
            <Text strong>{appointment.title}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Customer">
            {appointment.customer ?
              (appointment.customer.company_name || `${appointment.customer.first_name} ${appointment.customer.last_name}`)
              : 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Date & Time">
            {dayjs(appointment.start_time).format('MMMM DD, YYYY @ h:mm A')}
          </Descriptions.Item>
          <Descriptions.Item label="Technician">
            {appointment.technician_name || 'Unknown'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          notify_customer: true
        }}
      >
        <Title level={5}>Cancellation Details</Title>

        <Form.Item
          label="Reason for Cancellation"
          name="cancellation_reason"
          rules={[{ required: true, message: 'Please provide a cancellation reason' }]}
        >
          <TextArea
            rows={4}
            placeholder="e.g., Customer request, parts unavailable, technician emergency, weather conditions..."
          />
        </Form.Item>

        <Form.Item
          name="notify_customer"
          valuePropName="checked"
        >
          <Checkbox>
            <Text strong>Send cancellation notification to customer</Text>
            <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginLeft: 0 }}>
              (Recommended - Enabled by default)
            </Text>
          </Checkbox>
        </Form.Item>

        <Alert
          message="Customer Notification"
          description="When enabled, the customer will receive an email notification about the cancellation with the reason you provided. This helps maintain good customer communication and reduces confusion."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Divider />

        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={handleCancel}>
            Keep Appointment
          </Button>
          <Button
            danger
            type="primary"
            htmlType="submit"
            icon={<StopOutlined />}
            loading={submitting}
          >
            Cancel Appointment
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};
